import { Injectable, NgZone, DOCUMENT, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { Observable } from 'rxjs';
import { distinctUntilChanged, shareReplay, tap } from 'rxjs/operators';

export interface VisibleViewport {
  height: number;
  bottomInset: number;
}

@Injectable({
  providedIn: 'root'
})
export class VirtualKeyboardService {
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);

  private readonly viewport$: Observable<VisibleViewport> = new Observable<VisibleViewport>(subscriber => {
    const emit = () => this.zone.run(() => subscriber.next(this.measure()));
    const emitOnNextFrame = () => requestAnimationFrame(emit);

    emit();

    return this.zone.runOutsideAngular(() => this.addListeners(emit, emitOnNextFrame));
  }).pipe(
    distinctUntilChanged((prev, curr) => prev.height === curr.height && prev.bottomInset === curr.bottomInset),
    tap(({ bottomInset }) => this.document.documentElement.style.setProperty('--lms-keyboard-inset', `${bottomInset}px`)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public observe(): Observable<VisibleViewport> {
    return this.viewport$;
  }

  private measure(): VisibleViewport {
    const viewport = window.visualViewport;

    if (!viewport) {
      return { height: window.innerHeight, bottomInset: 0 };
    }

    const bottomInset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);

    return { height: viewport.height, bottomInset };
  }

  private addListeners(emit: () => void, emitOnNextFrame: () => void): () => void {
    const viewport = window.visualViewport;

    viewport?.addEventListener('resize', emit);
    viewport?.addEventListener('scroll', emit);
    window.addEventListener('orientationchange', emitOnNextFrame);

    const keyboardListeners = this.isKeyboardPluginAvailable()
      ? [
        Keyboard.addListener('keyboardDidShow', emitOnNextFrame),
        Keyboard.addListener('keyboardDidHide', emitOnNextFrame)
      ]
      : [];

    return () => {
      viewport?.removeEventListener('resize', emit);
      viewport?.removeEventListener('scroll', emit);
      window.removeEventListener('orientationchange', emitOnNextFrame);

      keyboardListeners.forEach(listener => listener.then(handle => handle.remove()));
      this.document.documentElement.style.removeProperty('--lms-keyboard-inset');
    };
  }

  private isKeyboardPluginAvailable(): boolean {
    return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Keyboard');
  }
}
